import SelectField from "controls/global/select-field";
import { getProductLocations, isValidDate } from "pages/file/utils/helper";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { SelectFieldOption } from "controls/global/select-field/SelectInput";
import { axiosSecuredInstance } from "configurations/axiosConfig";
import { useFilePropertyState } from "utils/context/FilePropertyStateContext";
import useCreateFile from "utils/custom-hooks/useCreateFile";
import useFormWrapper from "utils/custom-hooks/useFormWrapper";
import { formatDateToString } from "utils/shared";
import { mapUiCompanyStateLocation } from "pages/file/utils/companyHelper";
import CompanyStateLocation from "entities/UIModel/company/CompanyStateLocation";

export interface AgencyLocation extends SelectFieldOption {
  id: string;
  disabled?: boolean;
  locationUniqueID?: string;
  altaRegistryID?: string;
}

interface Props {
  productType: string;
  schema: string;
  name: string;
  label: string;
  disabled?: boolean;
  isRevising?: boolean;
  onChange?: () => void;
  defaultDateFieldName?: string;
}

const ProductAgencyLocationField = ({
  productType,
  schema,
  name,
  label,
  disabled,
  isRevising = false,
  onChange,
  defaultDateFieldName,
  ...rest
}: Props) => {
  const [disableLocal, setDisableLocal] = useState(true);
  const [productLocations, setProductLocations] = useState<AgencyLocation[] | null>(null);
  const { setValue, getValues, watch } = useFormWrapper();
  const { setProductLocationInfo } = useCreateFile();
  const abortControllerRef = useRef<AbortController | null>(null);

  // The file's original product location, captured once at mount. When it is not
  // part of the fetched list it is shown as a non-selectable option, and it must
  // remain available even after the user selects a different location — so it is
  // anchored here instead of to the live selection (watchedLocationName).
  const [originalLocation] = useState<string>(() => getValues(name) || "");

  // Captured at mount to detect when the effective date is changed on an existing
  // file (see isEffectiveDateChanged below).
  const [isExistingFile] = useState<boolean>(() => (getValues("id") || 0) > 0);
  const [originalDefaultDate] = useState(() => getValues(`${defaultDateFieldName}`));

  // The file's saved product location identity, captured at mount from pricingDetail.
  // Used to keep the location in the ${schema}.productLocations save-fallback list
  // when the API doesn't return it, so its identity fields survive the save.
  const [originalProductLocation] = useState(() => ({
    contractID: getValues(`${schema}.pricingDetail.contractID`) || "",
    legacyID: getValues(`${schema}.pricingDetail.legacyID`) || "",
    locationID: getValues(`${schema}.pricingDetail.locationID`) || "",
    locationLegalName: getValues(`${schema}.pricingDetail.locationLegalName`) || "",
    locationUniqueID: getValues(`${schema}.pricingDetail.locationUniqueID`) || "",
    locationDisplayName: getValues(name) || "",
  }));

  const [state] = useFilePropertyState();
  const agencyId = getValues("agency.id");
  const stateAbbr = getValues("properties.0.state.abbr");
  const watchedLocationName = watch(name);

  const defaultDate = getValues(`${defaultDateFieldName}`);

  // For an existing file, once the effective date is changed the dropdown must
  // list only what the API returns for the new date — the file's saved location
  // is no longer force-injected, and a now-invalid selection is reset.
  const isEffectiveDateChanged =
    isExistingFile &&
    isValidDate(defaultDate) &&
    isValidDate(originalDefaultDate) &&
    formatDateToString(defaultDate) !== formatDateToString(originalDefaultDate);

  const productLocationUniqueID = getValues(`${schema}.pricingDetail.locationUniqueID`);
  const orderStatusTypeCode = getValues(`${schema}.orderStatusTypeCode`);
  // const handleOnChange = (e: React.ChangeEvent<{}>, value: any) => {
  //   onChange && onChange();
  // };
  const handleOnChange = useCallback(
    (e: React.ChangeEvent<{}>, value: any) => {
      onChange && onChange();
    },
    [onChange]
  );  

  useEffect(() => {
    if (agencyId && stateAbbr && isValidDate(defaultDate)) {
      // Cancel previous request for this component instance only
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      const controller = new AbortController();
      abortControllerRef.current = controller;

      // Signal "loading" so the value-setting effect preserves the saved value
      // while this (re)fetch is in flight instead of validating against stale
      // data from a previous agency/state/date.
      setProductLocations(null);

      const getLocations = async () => {
        try {
          const stringDefaultDate = formatDateToString(defaultDate);
          const { data } = await axiosSecuredInstance.get(
            `/Company/GetLocationsByCompanyState`,
            {
              params: {
                CompanyID: agencyId,
                State: stateAbbr,
                DefaultDate: stringDefaultDate,
              },
              signal: controller.signal,
            }
          );
          const productLocations = mapUiCompanyStateLocation(data);

          // Keep the file's saved location in the save-fallback list when the API
          // didn't return it, so its identity fields (ContractID/LocationID/etc.)
          // still reach the backend on save. Skipped after an effective-date change
          // (the old location is intentionally dropped then).
          let fullProductLocations = productLocations;
          if (
            isExistingFile &&
            !isEffectiveDateChanged &&
            originalProductLocation.locationDisplayName
          ) {
            const exists = fullProductLocations.some(
              (l) =>
                (l.locationUniqueID &&
                  l.locationUniqueID === originalProductLocation.locationUniqueID) ||
                l.locationDisplayName === originalProductLocation.locationDisplayName
            );
            if (!exists) {
              fullProductLocations = [
                ...fullProductLocations,
                { ...originalProductLocation } as CompanyStateLocation,
              ];
            }
          }
          setValue(`${schema}.productLocations`, fullProductLocations);

          const agencyLocations: AgencyLocation[] = (data || [])
            .filter((location: any) => location.legacyID)
            .map((location: any) => ({
              text: location.locationDisplayName,
              value: location.locationDisplayName,
              id: location.companyID,
              disabled: location.isDisabled || false,
              locationUniqueID: location.locationUniqueID,
              altaRegistryID: location.altaRegistryID,
            }));
          setProductLocations(agencyLocations);
        } catch (error: any) {
          if (error?.name !== "AbortError" && error?.name !== "CanceledError") {
            console.error("Error fetching agency locations", error);
            setProductLocations([]);
          }
        }
      };

      getLocations();
      setDisableLocal(false);
    }
    else {
      setProductLocations([]);
      setDisableLocal(true);
    }

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [agencyId, stateAbbr, defaultDate]);  

  // Get product locations based on file location, except the disable location
  // Product locations will include the existing issued/voided product's location
  const dropDownOptions = useMemo(() => {
    return getProductLocations(productLocations || [], isEffectiveDateChanged ? "" : originalLocation, productLocationUniqueID) || [];
  }, [productLocations, originalLocation, productLocationUniqueID, isEffectiveDateChanged]);


  // Set product location and product location info
  useEffect(() => {
    const productLocationName = watchedLocationName; // getValues(name);

    // `null` => still loading; `[]` => the API returned no locations (which can
    // be a transient/initial empty response followed by a populated one). In both
    // cases preserve the saved value — defaulting/validating here would wipe a
    // valid location before the real data arrives.
    if (!productLocations || productLocations.length === 0) {
      return;
    }

    // Default only when the fetched list has exactly one item. Otherwise, match
    // the product's own saved location (originalLocation) in the list — select it
    // if present, else leave the field blank rather than defaulting to anything.
    if (!productLocationName) {
      if (productLocations.length === 1) {
        setValue(name, productLocations[0].value, { shouldValidate: true });
      } else {
        const foundOriginal = productLocations.find(
          (location) => location.value === originalLocation
        );
        setValue(name, foundOriginal ? originalLocation : "", { shouldValidate: true });
      }
    }
    // When productLocationName is set but not in the fetched list we intentionally
    // preserve it instead of blanking. getProductLocations re-adds it as a disabled
    // (non-selectable) option so the file's existing location stays visible.
    // Exception: if the effective date was changed on an existing file, the saved
    // location is no longer valid for the new date — clear it so it re-defaults
    // from the newly fetched list.
    else {
      const found = productLocations.find(
        (location) => location.value === productLocationName
      );
      if (!found && isEffectiveDateChanged) {
        setValue(name, "");
      }
    }

    // set product location info
    const foundLocation = productLocations.find(
      (location) => location.value === productLocationName
    );

    if (foundLocation) {
      // Set location information as it's needed in api to build serial number
      setProductLocationInfo(productType, schema, foundLocation, isRevising, orderStatusTypeCode);
    }

  }, [watchedLocationName, productLocations, state, isRevising, isEffectiveDateChanged, originalLocation]);

  // // debug rerender
  // useEffect(() => {
  //   console.log("Component: ProductAgencyLocationField -  rendered", name);
  // });  

  return (
    <div style={{ position: "relative" }} className="product-agency-location">
      <SelectField
        name={name}
        label={label}
        options={dropDownOptions}
        onChange={handleOnChange}
        disabled={disabled || disableLocal}
        {...rest}
      />
    </div>
  );
};

export default React.memo(ProductAgencyLocationField);
